import type { ConversionResult } from '../converter/conversion-controller'
import { createZip, blobToUint8Array } from '../zip/zip-writer'
import { formatBytes } from '../utils/format'
import { revokeObjectURL } from '../utils/memory'

/**
 * Renders the result screen showing conversion summary and
 * a download button. For single-page results, downloads the
 * image directly. For multi-page, creates a ZIP.
 * Calls onConvertAnother when the user clicks "Convert another PDF".
 */
export async function createResultDisplay(
  container: HTMLElement,
  result: ConversionResult,
  onConvertAnother: () => void,
): Promise<void> {
  const { blobs, totalSize, format, baseName, skippedPages } = result
  const count = blobs.length
  const ext = format === 'jpeg' ? 'jpg' : 'png'
  const isSingle = count === 1
  const skippedNote =
    skippedPages.length > 0
      ? `<p class="result-skipped">${skippedPages.length} ${skippedPages.length === 1 ? 'page was' : 'pages were'} skipped</p>`
      : ''

  if (count === 0) {
    container.innerHTML = `
      <div class="result-panel">
        <h2 class="result-title">No images converted</h2>
        <p class="result-summary">
          All ${skippedPages.length} ${skippedPages.length === 1 ? 'page was' : 'pages were'} skipped.
        </p>
        <button type="button" class="convert-another-button" id="convertAnotherButton">
          Convert another PDF
        </button>
      </div>
    `

    const convertAnotherButton =
      container.querySelector<HTMLButtonElement>('#convertAnotherButton')!
    convertAnotherButton.addEventListener('click', onConvertAnother)
    return
  }

  let downloadUrl: string
  let downloadName: string

  if (isSingle) {
    downloadUrl = URL.createObjectURL(blobs[0].blob)
    downloadName = blobs[0].name
  } else {
    const files = await Promise.all(
      blobs.map(async (b) => ({
        name: b.name,
        data: await blobToUint8Array(b.blob),
      })),
    )
    const zipData = createZip(files)
    downloadUrl = URL.createObjectURL(
      new Blob([zipData as BlobPart], { type: 'application/zip' }),
    )
    downloadName = `${baseName}-${ext}.zip`
  }

  container.innerHTML = `
    <div class="result-panel">
      <h2 class="result-title">Conversion complete</h2>
      <p class="result-summary">
        ${count} ${count === 1 ? 'image' : 'images'} · ${formatBytes(totalSize)}
      </p>
      ${skippedNote}
      <button type="button" class="download-button" id="downloadButton">
        Download ${isSingle ? 'Image' : 'ZIP'}
      </button>
      <button type="button" class="convert-another-button" id="convertAnotherButton">
        Convert another PDF
      </button>
    </div>
  `

  const downloadButton =
    container.querySelector<HTMLButtonElement>('#downloadButton')!
  const convertAnotherButton =
    container.querySelector<HTMLButtonElement>('#convertAnotherButton')!

  downloadButton.addEventListener('click', () => {
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = downloadName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  })

  convertAnotherButton.addEventListener('click', () => {
    revokeObjectURL(downloadUrl)
    onConvertAnother()
  })
}
